import { useState, useEffect } from 'react';
import { useLoyalty } from '../../../contexts/LoyaltyContext';
import { apiGet } from '../../../lib/api';
import { ContestPlayModal } from './ContestPlayModal';

interface Contest {
  id: string;
  title: string;
  description: string | null;
  imageUrl: string | null;
  contestType: string;
  type?: string;
  startDate: string;
  endDate: string;
  endsAt: string;
  entryFee: number;
  status: string;
  config: Record<string, unknown>;
  entryConfig: Record<string, unknown>;
  winValidation?: string;
  rulesUrl?: string | null;
}

interface LoyaltyContestsProps {
  title?: string;
  maxItems?: number;
  showFriendStandings?: boolean;
}

function formatCountdown(dateStr: string): string {
  const diff = new Date(dateStr).getTime() - Date.now();
  if (diff <= 0) return 'ENDED';
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  if (days > 0) return `ENDS IN ${days} DAY${days > 1 ? 'S' : ''}`;
  return `ENDS IN ${hours}H`;
}

const TYPE_CTA: Record<string, string> = {
  instant_win: 'Play Now',
  draw: 'Enter Draw',
  leaderboard: 'View Standings',
};

function AvatarSmall({ firstName, lastName, avatarUrl }: { firstName: string | null; lastName: string | null; avatarUrl: string | null }) {
  const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || '?';

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={`${firstName ?? ''} ${lastName ?? ''}`}
        className="w-8 h-8 rounded-full object-cover border border-[var(--color-surface-border)]"
      />
    );
  }

  return (
    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold bg-[var(--color-primary)] text-white">
      {initials}
    </div>
  );
}

function LoginCta() {
  return (
    <div className="loyalty-card p-8 text-center">
      <div className="text-4xl mb-3 opacity-50">&#127941;</div>
      <h2 className="loyalty-heading text-xl mb-2 text-[var(--color-text)]">
        Login to view contests
      </h2>
      <p className="text-[var(--color-text-muted)] text-sm mb-4">
        Sign in to join active contests and earn rewards.
      </p>
      <a
        href="/login"
        className="inline-block px-6 py-2 rounded-lg bg-[var(--color-primary)] text-white font-semibold text-sm hover:opacity-90 transition-opacity"
      >
        Sign In
      </a>
    </div>
  );
}

export function LoyaltyContestsBlock({ props }: { props: LoyaltyContestsProps }) {
  const { data, loading: loyaltyLoading } = useLoyalty();
  const maxItems = props.maxItems ?? 6;
  const showFriendStandings = props.showFriendStandings ?? false;

  const [contests, setContests] = useState<Contest[]>([]);
  const [loading, setLoading] = useState(false);
  const [playingContest, setPlayingContest] = useState<Contest | null>(null);

  useEffect(() => {
    if (!data) return;
    let cancelled = false;
    setLoading(true);

    const pid = data?.programId;
    if (!pid) return;
    apiGet<Contest[]>(`/customer/loyalty/contests?programId=${pid}`)
      .then((res) => {
        if (!cancelled) setContests(Array.isArray(res) ? res : []);
      })
      .catch(() => {
        if (!cancelled) setContests([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [data]);

  if (loyaltyLoading) {
    return (
      <div className="loyalty-card p-8 animate-pulse">
        <div className="h-8 w-64 bg-[var(--color-surface-border)] rounded mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-64 bg-[var(--color-surface-border)] rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (!data) return <LoginCta />;

  const { profile, stats, friendStandings } = data;
  const displayContests = contests.slice(0, maxItems);
  const topFriends = friendStandings.slice(0, 3);

  return (
    <div className="space-y-6">
      {/* Hero greeting */}
      <div className="loyalty-card p-6 sm:p-8">
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1">
            <h1 className="loyalty-heading text-2xl sm:text-3xl text-[var(--color-text)] mb-2">
              GAME ON, {profile.firstName.toUpperCase()}!
            </h1>
            <div className="flex items-center gap-4 text-sm text-[var(--color-text-muted)]">
              {stats.globalRank && (
                <span>
                  Rank <span className="font-bold text-[var(--color-primary)]">#{stats.globalRank}</span>
                </span>
              )}
              <span>
                Season Score: <span className="font-bold text-[var(--color-primary)]">{profile.pointsBalance.toLocaleString()}</span>
              </span>
            </div>
          </div>

          {/* Friend standings sidebar */}
          {showFriendStandings && topFriends.length > 0 && (
            <div className="lg:w-64 bg-[var(--color-surface)] rounded-xl p-4">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[var(--color-text-muted)] mb-3">
                Friend Standings
              </h3>
              <div className="space-y-2">
                {topFriends.map((friend) => (
                  <div key={friend.customerId} className="flex items-center gap-2 text-sm">
                    <span className="text-[var(--color-text-muted)] w-5 text-right font-bold">
                      #{friend.rank}
                    </span>
                    <AvatarSmall
                      firstName={friend.firstName}
                      lastName={friend.lastName}
                      avatarUrl={friend.avatarUrl}
                    />
                    <span className="flex-1 truncate text-[var(--color-text)]">
                      {friend.firstName} {friend.lastName?.[0]}.
                    </span>
                    <span className="font-bold text-[var(--color-primary)] text-xs">
                      {friend.points.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Active Contests */}
      <div>
        <h2 className="loyalty-heading text-lg sm:text-xl text-[var(--color-text)] mb-4">
          {props.title ?? 'ACTIVE CONTESTS'}
        </h2>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-pulse">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-64 bg-[var(--color-surface-border)] rounded-xl" />
            ))}
          </div>
        ) : displayContests.length === 0 ? (
          <div className="loyalty-card p-8 text-center">
            <p className="text-[var(--color-text-muted)] text-sm">
              No active contests right now. Check back soon!
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayContests.map((contest) => {
              const cType = contest.contestType ?? contest.type ?? 'instant_win';
              const ctaLabel = TYPE_CTA[cType] ?? 'Play Now';
              const dateStr = contest.endsAt ?? contest.endDate;

              return (
                <div
                  key={contest.id}
                  className="loyalty-card overflow-hidden flex flex-col group hover:shadow-lg transition-shadow"
                >
                  {/* Image area */}
                  <div className="relative h-40 bg-[var(--color-surface)]">
                    {contest.imageUrl ? (
                      <img
                        src={contest.imageUrl}
                        alt={contest.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-4xl opacity-30">
                        &#127942;
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    {/* Countdown badge */}
                    {dateStr && (
                      <span className="absolute top-3 right-3 loyalty-tag-skew text-[10px] font-bold px-2 py-1 rounded bg-[var(--color-primary)] text-white">
                        {formatCountdown(dateStr)}
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4 flex-1 flex flex-col">
                    <h3 className="font-bold text-[var(--color-text)] text-sm mb-1 line-clamp-2">
                      {contest.title}
                    </h3>
                    {contest.description && (
                      <p className="text-xs text-[var(--color-text-muted)] mb-3 line-clamp-2">
                        {contest.description}
                      </p>
                    )}
                    <div className="mt-auto flex items-center justify-between">
                      <span className="text-xs font-bold text-[var(--color-text-muted)]">
                        {contest.entryFee > 0
                          ? `${contest.entryFee.toLocaleString()} Points`
                          : 'FREE'}
                      </span>
                      <button
                        onClick={() => setPlayingContest(contest)}
                        className="px-4 py-1.5 rounded-lg bg-[var(--color-primary)] text-white text-xs font-bold hover:opacity-90 active:scale-[0.98] transition-all"
                      >
                        {ctaLabel}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Play modal */}
      {playingContest && (
        <ContestPlayModal
          contest={playingContest}
          onClose={() => setPlayingContest(null)}
        />
      )}
    </div>
  );
}
